import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';
import { IconButton, Tooltip, Typography, makeStyles, useMediaQuery } from '@material-ui/core';
import Archive from '@material-ui/icons/Archive';
import Label from '@material-ui/icons/Label';
import Unarchive from '@material-ui/icons/Unarchive';
import AskReviewIcon from '@material-ui/icons/RateReview';
import Review from 'modules/conversations/components/Review';
import NotificationTooltip from 'components/shared/NotificationTooltip';
import AskReviewDialog from './AskReviewDialog';
import TagsDialog from './TagsDialog';

const styles = makeStyles((theme) => ({
  iconbutton: {
    [theme.breakpoints.down('sm')]: {
      padding: '12px 4px',
    },
  },
  arrow: {
    color: 'rgba(97, 97, 97, 0.5)',
  },
  popper: {
    zIndex: '1301 !important',
  },
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    border: '1px solid rgba(97, 97, 97, 0.2)',
  },
  container: {
    [theme.breakpoints.down('xs')]: {
      '& svg': {
        width: '0.8em',
      },
    },
  },
}));

const TabRightMenu = ({
  currentUser,
  otherUsers,
  cottagesToAskForReview,
  isHost,
  shouldOpenAskReviewNotification,
  askForReviewNotificationClick,
  askReviewDialogOpen,
  setAskReviewDialogOpen,
  askForReview,
  tags,
  toggleArchived,
  data,
  isTagOpen,
  setIsTagOpen,
  changeTags,
  reviewHost,
}) => {
  const classes = styles();
  const isSmall = useMediaQuery('(max-width:600px)');

  return (
    <div className={classes.container}>
      <TagsDialog
        {...{
          isTagOpen,
          setIsTagOpen,
          data,
          changeTags,
          tags,
        }}
      />
      {!!(cottagesToAskForReview && cottagesToAskForReview.length) && isHost && (
        <AskReviewDialog
          open={askReviewDialogOpen}
          handleClose={() => setAskReviewDialogOpen(false)}
          cottages={cottagesToAskForReview}
          onSubmit={askForReview}
        />
      )}
      {!currentUser.has_reviewed_cottages && otherUsers[0] && (
        <Review
          open={!!(data.Messages.length > 1 && !data.HostReview && !isSmall)}
          host={otherUsers[0]}
          onSubmit={reviewHost}
          initialValues={
            data.HostReview || {
              recommended: null,
              details: {},
              extra: '',
            }
          }
        />
      )}
      {!!(cottagesToAskForReview && cottagesToAskForReview.length) && isHost && (
        <NotificationTooltip
          title={
            <>
              {shouldOpenAskReviewNotification && (
                <Typography align="center">
                  <FormattedMessage
                    id="You can now ask the guest to leave your ad a review"
                    defaultMessage="You can now ask the guest to leave your ad a review"
                  />
                </Typography>
              )}
              {!shouldOpenAskReviewNotification && (
                <FormattedMessage id="Ask for review" defaultMessage="Ask for review" />
              )}
            </>
          }
          startOpen={shouldOpenAskReviewNotification}
          classes={
            shouldOpenAskReviewNotification
              ? { arrow: classes.arrow, tooltip: classes.tooltip, popper: classes.popper }
              : { popper: classes.popper }
          }
          showButton={shouldOpenAskReviewNotification}
          onButtonClick={askForReviewNotificationClick}
        >
          <IconButton
            onClick={() => setAskReviewDialogOpen(!askReviewDialogOpen)}
            className={classes.iconbutton}
          >
            <AskReviewIcon />
          </IconButton>
        </NotificationTooltip>
      )}
      <IconButton disabled={!tags.length} onClick={toggleArchived} className={classes.iconbutton}>
        {data.tags && !!data.tags.find(({ id }) => id === 3) ? (
          <Tooltip
            key="unarchiveTooltip"
            title={<FormattedMessage id="Unarchive" defaultMessage="Unarchive" />}
          >
            <Unarchive />
          </Tooltip>
        ) : (
          <Tooltip
            key="archiveTooltip"
            title={<FormattedMessage id="Archive" defaultMessage="Archive" />}
          >
            <Archive />
          </Tooltip>
        )}
      </IconButton>
      <Tooltip title={<FormattedMessage id="Add tags" defaultMessage="Add tags" />}>
        <span>
          <IconButton
            onClick={() => setIsTagOpen(true)}
            disabled={isEmpty(data)}
            className={classes.iconbutton}
          >
            <Label />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
};

TabRightMenu.propTypes = {
  currentUser: PropTypes.shape({
    has_reviewed_cottages: PropTypes.bool,
  }),
  otherUsers: PropTypes.arrayOf(PropTypes.shape({})),
  cottagesToAskForReview: PropTypes.arrayOf(PropTypes.shape({})),
  isHost: PropTypes.bool,
  shouldOpenAskReviewNotification: PropTypes.bool,
  askForReviewNotificationClick: PropTypes.func,
  askReviewDialogOpen: PropTypes.bool,
  setAskReviewDialogOpen: PropTypes.func,
  askForReview: PropTypes.func,
  tags: PropTypes.arrayOf(PropTypes.shape({})),
  toggleArchived: PropTypes.func,
  data: PropTypes.shape({
    Messages: PropTypes.arrayOf(PropTypes.shape({})),
    HostReview: PropTypes.shape({}),
    tags: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isTagOpen: PropTypes.bool,
  setIsTagOpen: PropTypes.func,
  changeTags: PropTypes.func,
  reviewHost: PropTypes.func,
};

export default TabRightMenu;
